import { Component, OnInit, OnDestroy, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import {
  VideoRecoderService,
  CommentRecording,
} from 'src/app/services/video-recorder/video-recoder.service';
import {
  InterviewAnswer,
  Question,
} from '../../models/virtual-interview-model';
import { InterviewService } from '../../services/interview.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-interview-session',
  templateUrl: './interview-session.component.html',
  styleUrls: ['./interview-session.component.css'],
})
export class InterviewSessionComponent implements OnInit, OnDestroy {
  showTips: boolean = true;
  questions: Question[] = [];
  sessionId: string = '';
  isLoadingQuestions: boolean = true;

  interviewAnswers: InterviewAnswer[] = [];
  progressValueCurrentValue: number = 0;
  totalSeconds: number = 3600;
  initialTotalSeconds: number = 3600;
  secondsElapsed: number = 0;
  progressInterval: any;

  currentQuestionIndex = 0;
  isStartVr = false;
  isVideoOn = false;
  isMicOn = false;

  isCommentRecording = false;
  commentTimeRemaining = 30;
  commentsRemaining = 3;
  currentQuestionComments: CommentRecording[] = [];
  commentTimesPerQuestion: { [key: number]: number } = {};

  currentSessionIndex: number = 0;
  language: string = 'en';

  private subs: Subscription[] = [];

  constructor(
    public recorderService: VideoRecoderService,
    private zone: NgZone,
    private router: Router,
    private interviewService: InterviewService
  ) {
    const navigation = this.router.getCurrentNavigation();
    if (navigation?.extras?.state) {
      this.sessionId = navigation.extras.state['sessionId'];
      this.currentSessionIndex = navigation.extras.state['sessionIndex'] || 0;
    }
  }

  // ─── Helper ───────────────────────────────────────────────────────────────
  isRTL(): boolean {
    return this.language === 'ar';
  }

  private t(en: string, ar: string): string {
    return this.isRTL() ? ar : en;
  }
  // ──────────────────────────────────────────────────────────────────────────

  ngOnInit(): void {
    if (!this.sessionId) {
      Swal.fire({
        icon: 'error',
        title: this.t('Session Not Found', 'الجلسة غير موجودة'),
        text: this.t('No session found. Redirecting...', 'لم يتم العثور على جلسة. جارٍ إعادة التوجيه...'),
        confirmButtonText: this.t('OK', 'حسناً'),
      }).then(() => {
        this.router.navigate(['/virtual-interview-steps']);
      });
      return;
    }

    this.loadSessionQuestions();

    this.subs.push(
      this.recorderService.isVideoOn$.subscribe((v) => (this.isVideoOn = v)),
      this.recorderService.isMicOn$.subscribe((v) => (this.isMicOn = v)),

      this.recorderService.isCommentRecording$.subscribe((recording) => {
        this.zone.run(() => {
          this.isCommentRecording = recording;
        });
      }),

      this.recorderService.commentTimeRemaining$.subscribe((time) => {
        this.zone.run(() => {
          this.commentTimeRemaining = time;
        });
      }),

      this.recorderService.recordedComment$.subscribe((comment) => {
        if (comment) {
          this.zone.run(() => {
            this.currentQuestionComments.push(comment);
          });
        }
      })
    );
  }

  loadSessionQuestions() {
    this.isLoadingQuestions = true;

    this.interviewService.getSessionDetails(this.sessionId).subscribe({
      next: (sessionData) => {
        this.language = sessionData.language || 'en';

        this.questions = sessionData.questionsInfo.map((q: any, index: number) => ({
          text: q.interviewVideo?.questionText || `Question ${index + 1}`,
          explanation: undefined,
          hasExplanation: q.explained || false,
          explained: q.explained,
          interviewVideo: q.interviewVideo,
          recordedVideos: q.recordedVideos,
        }));

        this.commentsRemaining = sessionData.remainingAdditionalAttempts;
        this.recorderService.commentsRemaining$.next(sessionData.remainingAdditionalAttempts);

        const remainingTime = sessionData.remainingSessionTime || 3600;
        this.totalSeconds = remainingTime;
        this.initialTotalSeconds = 3600;
        this.secondsElapsed = 3600 - remainingTime;
        this.progressValueCurrentValue = (this.secondsElapsed / this.initialTotalSeconds) * 100;

        this.interviewAnswers = this.questions.map((q: any, i: number) => ({
          index: i,
          question: q.text || '',
          videoBlob: null,
          videoUrl: null,
          duration: 0,
          answeredAt: null,
          comments: [],
          recordedVideoId: null,
        }));

        this.isLoadingQuestions = false;
      },
      error: (error) => {
        Swal.fire({
          icon: 'error',
          title: this.t('Failed to Load Questions', 'فشل تحميل الأسئلة'),
          text: this.t(
            'Could not load interview questions. Please try again.',
            'تعذّر تحميل أسئلة المقابلة. يرجى المحاولة مجدداً.'
          ),
          confirmButtonText: this.t('OK', 'حسناً'),
        }).then(() => {
          this.isLoadingQuestions = false;
          this.router.navigate(['/virtual-interview-steps']);
        });
      },
    });
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
    this.stopProgressTimer();
    this.recorderService.finalizeAndCleanup();
  }

  toggleCamera() {
    this.recorderService.toggleVideoStream();
  }

  toggleMic() {
    this.recorderService.toggleAudioStream();
  }

  startInterview() {
    this.isStartVr = true;
    this.recorderService.commentsRemaining$.next(this.commentsRemaining);
    this.recorderService.resetCommentSession();
    this.startProgressTimer();
  }

  startProgressTimer() {
    this.progressInterval = setInterval(() => {
      this.secondsElapsed++;
      this.progressValueCurrentValue =
        (this.secondsElapsed / this.initialTotalSeconds) * 100;

      if (this.progressValueCurrentValue >= 100) {
        this.progressValueCurrentValue = 100;
        this.stopProgressTimer();
        this.showTimeUpWarning();
      }
    }, 1000);
  }

  stopProgressTimer() {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }
  }

  showTimeUpWarning() {
    Swal.fire({
      icon: 'warning',
      title: this.t('Session Time Expired', 'انتهى وقت الجلسة'),
      text: this.t(
        'Your interview session time has ended. Please submit your answers.',
        'لقد انتهى وقت جلسة المقابلة. يرجى تسليم إجاباتك.'
      ),
      confirmButtonText: this.t('OK', 'حسناً'),
    });
  }

  startCommentRecording() {
    if (this.commentsRemaining > 0) {
      this.recorderService.startCommentRecording();
    } else {
      Swal.fire({
        icon: 'warning',
        title: this.t('No Comments Remaining', 'لا تتبقى تعليقات'),
        text: this.t(
          'You have used all 3 comments for this session.',
          'لقد استخدمت جميع التعليقات الـ 3 لهذه الجلسة.'
        ),
        confirmButtonText: this.t('OK', 'حسناً'),
      });
    }
  }

  stopCommentRecording() {
    this.recorderService.stopCommentRecording();
  }

  cancelCommentRecording() {
    this.recorderService.cancelCommentRecording();
  }

  onCommentAdded() {
    this.zone.run(() => {
      if (this.commentsRemaining > 0) {
        this.commentsRemaining--;
        this.recorderService.commentsRemaining$.next(this.commentsRemaining);
      }
    });
  }

  handleNextQuestion(answerData: any) {
    this.zone.run(() => {
      const answer = this.interviewAnswers[this.currentQuestionIndex];
      answer.duration = answerData.duration;
      answer.videoBlob = answerData.videoBlob;
      answer.videoUrl = answerData.videoUrl;
      answer.answeredAt = new Date();
      answer.recordedVideoId = answerData.recordedVideoId;

      answer.comments = this.currentQuestionComments;

      if (this.currentQuestionIndex < this.questions.length - 1) {
        this.currentQuestionIndex++;
        this.currentQuestionComments = [];
        this.questions = [...this.questions];
      } else {
        this.finishInterview();
      }
    });
  }

  finishInterview() {
    const totalComments = this.interviewAnswers.reduce(
      (sum, answer) => sum + (answer.comments?.length || 0),
      0
    );

    this.interviewService.submitInterview(this.sessionId).subscribe({
      next: (response) => {
        if (this.currentSessionIndex === 0) {
          Swal.fire({
            icon: 'success',
            title: this.t('Interview Submitted Successfully!', 'تم تسليم المقابلة بنجاح!'),
            html: this.isRTL()
              ? `
                <p>تم إكمال محاولتك الأولى في المقابلة.</p>
                <p>ستكون نتائج تقييمك متاحة في ملفك الشخصي خلال ساعتين.</p>
                <p>يمكنك الاطلاع على نتائجك من قسم تدريب المقابلة في ملفك الشخصي أو من صفحة الخطوات.</p>
              `
              : `
                <p>Your first interview attempt has been completed.</p>
                <p>Your evaluation results will be available in your profile within 2 hours.</p>
                <p>You can view your results from the Interview Training section in your profile or from the steps page.</p>
              `,
            confirmButtonText: this.t('Go to Home', 'الذهاب إلى الرئيسية'),
          }).then(() => {
            this.router.navigate(['/']);
          });
        } else if (this.currentSessionIndex === 1) {
          Swal.fire({
            icon: 'success',
            title: this.t('Interview Completed!', 'اكتملت المقابلة!'),
            html: this.isRTL()
              ? `
                <p>تهانينا! لقد أتممت محاولتك الثانية في المقابلة.</p>
                <p>ستكون نتائج تقييمك متاحة في ملفك الشخصي خلال ساعتين.</p>
                <p>يمكنك الاطلاع على نتائجك من قسم تدريب المقابلة في ملفك الشخصي.</p>
              `
              : `
                <p>Congratulations! You have completed your second interview attempt.</p>
                <p>Your evaluation results will be available in your profile within 2 hours.</p>
                <p>You can view your results from the Interview Training section in your profile.</p>
              `,
            confirmButtonText: this.t('Go to Home', 'الذهاب إلى الرئيسية'),
          }).then(() => {
            this.router.navigate(['/']);
          });
        } else {
          this.router.navigate(['/']);
        }
      },
      error: (error) => {
        Swal.fire({
          icon: 'error',
          title: this.t('Submission Failed', 'فشل التسليم'),
          text: this.t(
            'Failed to submit interview. Please try again.',
            'فشل تسليم المقابلة. يرجى المحاولة مجدداً.'
          ),
          confirmButtonText: this.t('OK', 'حسناً'),
        });
      },
    });
  }

  formatTimeRemaining(seconds: number): string {
    return `${seconds}s`;
  }

  getFormattedRemainingTime(): string {
    const remainingSeconds = this.initialTotalSeconds - this.secondsElapsed;
    const minutes = Math.floor(remainingSeconds / 60);
    const seconds = remainingSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
}